import React from 'react';
import { Card } from 'antd';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';

import { CompressorActivityStateEnum } from '@/constants/enums';
import { useMeasurement } from '@/hooks/useMeasurement';
import DetailsWidgetSlider from '@/comp/StationControlCenter/ControlCenterCompressorWidget/DetailsWidgetSlider/DetailsWidgetSlider';
import { MetricItem } from '@/comp/StationControlCenter/UniversalDeviceWidget/components/Metrics/MetricItem/MetricItem';
import { MetricsContainer, SliderContainer } from './RealtimeDataSection.styles';
import type { DeviceDrawerSchema } from '@/comp/StationControlCenter/types/device-schema.types';
import type { Device } from '@/store/Station/constants';
import { buildRealtimeDataSectionVM } from './RealtimeDataSection.utils';

interface RealtimeDataSectionProps {
  device: Device;
  schema?: DeviceDrawerSchema;
}

type DeviceWithActivity = Device & { activity?: string };

const RealtimeDataSection = observer(({ device, schema }: RealtimeDataSectionProps) => {
  const { t } = useTranslation();
  const { tvalue, tunit } = useMeasurement();

  const { sliderMetric, sliderPoints, hasContent, sliderTitle, metricItems } =
    buildRealtimeDataSectionVM({
      device,
      schema,
      t,
      measurement: { tvalue, tunit },
    });

  if (!hasContent) return null;

  return (
    <Card title={t('realTimeData')}>
      {sliderMetric && (
        <SliderContainer>
          <DetailsWidgetSlider
            title={sliderTitle}
            value={tvalue(Number(sliderMetric.value), sliderMetric.unit)}
            unit={t(tunit(sliderMetric.unit))}
            min={
              sliderMetric.additionalContext?.min?.value !== undefined
                ? tvalue(sliderMetric.additionalContext.min.value, sliderMetric.unit)
                : undefined
            }
            max={
              sliderMetric.additionalContext?.max?.value !== undefined
                ? tvalue(sliderMetric.additionalContext.max.value, sliderMetric.unit)
                : undefined
            }
            points={sliderPoints}
            connectivity={device.connectivity}
            highlightSlider={
              (device as DeviceWithActivity).activity?.toLowerCase() === CompressorActivityStateEnum.Load
            }
            addSideGaps
            hasTarget={sliderMetric.additionalContext?.target !== undefined}
            deviceType={device.deviceType}
          />
        </SliderContainer>
      )}
      {metricItems.length > 0 && (
        <MetricsContainer
          $hasSlider={Boolean(sliderMetric)}
          className="label-value-container real-time-data-container"
        >
          {metricItems.map((item, index) => (
            <MetricItem
              key={`${item.label}-${index}`}
              variant="drawer"
              label={item.label}
              value={item.value}
              unit={item.unit}
            />
          ))}
        </MetricsContainer>
      )}
    </Card>
  );
});

export default RealtimeDataSection;

